import React, { MouseEvent } from 'react';
import styled from '@emotion/styled';
import queryString from 'query-string';
import { css } from '@emotion/react';

// components
import GoogleLogo from 'js/common/components/GoogleLogo';
import GithubLogo from 'js/common/components/GithubLogo';
import Button from '@leafygreen-ui/button';

// styles
import { mq } from 'js/common/utils/mediaQueries';
import { thirdPartyColors } from 'js/auth/utils/palette';
import { palette } from '@leafygreen-ui/palette';

// types
import { Search } from 'js/auth/types/search';
import { getSignUpSource } from 'js/auth/types/signup';
import { PartnerType } from 'js/common/types/billing';

// css variables
const buttonSize = 52;

export enum SocialAuthProvider {
  GOOGLE = 'Google',
  GITHUB = 'GitHub',
}

const socialTypeToStyle = new Map<string, { boxShadow: string; borderColor: string }>([
  [
    'Google',
    {
      boxShadow: `0px 0px 0px 1.5px ${thirdPartyColors.googleBtnBackgroundBase} inset`,
      borderColor: `${thirdPartyColors.googleBtnBackgroundBase}`,
    },
  ],
  [
    'GitHub',
    {
      boxShadow: `0px 0px 0px 3px ${thirdPartyColors.githubBtnBackgroundHover}`,
      borderColor: `${thirdPartyColors.githubBtnDisabled}`,
    },
  ],
]);

const socialButtonStyle = (props: { disabled: boolean; text: string }) =>
  props.disabled
    ? css(
        mq({
          backgroundColor: thirdPartyColors.githubBtnBackgroundDisabled,
          border: `1px solid ${palette.gray.light1}`,
          boxSizing: 'border-box',
          padding: '0 2px 0 0',
          marginTop: '16px',
          borderRadius: '6px',
          width: [296, 'calc(100vw - 36px)', 296, 296],
          height: `${buttonSize}px`,
        })
      )
    : css(
        mq({
          backgroundColor: thirdPartyColors.githubBtnBackground,
          display: 'flex',
          flexDirection: 'row',
          padding: '0 2px 0 0',
          marginTop: '16px',
          borderRadius: '6px',
          width: [296, 'calc(100vw - 36px)', 296, 296],
          height: `${buttonSize}px`,
          boxShadow: '0px 1px lightgray',
          ':hover, :focus': {
            boxShadow: socialTypeToStyle.get(props.text)?.boxShadow,
            borderColor: socialTypeToStyle.get(props.text)?.borderColor,
          },
        })
      );

const DisplayText = styled.div({
  color: palette.gray.dark1,
});

interface SocialAuthButtonProps {
  windowLocation?: Pick<Location, 'assign'>;
  location?: Pick<Location, 'search'>;
  socialAuthProvider?: SocialAuthProvider;
  disabled?: boolean;
  className?: string;
  clientState?: Search;
  marketplaceLinkFlow?: PartnerType;
}

export default function SocialAuthButton({
  windowLocation = window.location,
  location: { search } = { search: '' },
  socialAuthProvider = SocialAuthProvider.GOOGLE,
  disabled = false,
  marketplaceLinkFlow,
  clientState = {},
}: SocialAuthButtonProps) {
  const onClick = async (e: MouseEvent) => {
    e.preventDefault();
    const { isVercelIntegration } = clientState;
    const signupSource = getSignUpSource(marketplaceLinkFlow, isVercelIntegration);
    const queryParams = { ...(queryString.parse(search) as Search), signupSource, ...clientState };
    const socialIdp = socialAuthProvider.toLowerCase();
    const ssoUrl = `/account/sso/${socialIdp}?${queryString.stringify(queryParams)}`;
    windowLocation.assign(ssoUrl);
  };

  const displayLogo = (socialAuthProvider: SocialAuthProvider) => {
    if (socialAuthProvider == SocialAuthProvider.GOOGLE) {
      return <GoogleLogo size={buttonSize} />;
    } else if (socialAuthProvider == SocialAuthProvider.GITHUB) {
      return (
        <GithubLogo fillColor={disabled ? thirdPartyColors.githubBtnDisabled : thirdPartyColors.githubBtnBorder} />
      );
    }
  };

  return (
    <Button
      disabled={disabled}
      baseFontSize={16}
      css={socialButtonStyle({ disabled: disabled, text: socialAuthProvider })}
      onClick={onClick}
    >
      {displayLogo(socialAuthProvider)}
      <DisplayText data-testid="displayText">{socialAuthProvider}</DisplayText>
    </Button>
  );
}
